import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Aviso,
  CartaoIndicador,
  CelulaDeTotal,
  EstadoCarregando,
  EstadoErro,
  EstadoVazio,
  Selo,
  Tabela,
  ValorFiscal,
  ValorPercentual,
  type ColunaTabela,
} from '../ui';
import { usePainelDoParticipante } from '../../hooks/usePainel';
import { mensagemDoErro } from '../../servicos/erros';
import type { LinhaDoParticipante, PainelDoParticipante } from '../../servicos/painel';
import type { DashboardIcms } from '../../servicos/perfil';
import {
  CartaoMesesAproveitados,
  NotaDeMetricasIndisponiveis,
  SeloDeContrato,
  TituloDeSecao,
} from './comum';

/**
 * Dashboard de participante — assessor e fornecedor.
 *
 * ```
 * GET /api/icms/dashboard/assessor/:id/:ano
 * GET /api/icms/dashboard/fornecedor/:id/:ano
 * ```
 *
 * As duas rotas devolvem o **mesmo formato**: uma linha por par cliente ×
 * fornecedor e um bloco `geral`. Nada de série de 12 meses — por isso este
 * componente é separado do painel do cliente em vez de uma prop a mais nele.
 *
 * O que muda entre assessor e fornecedor é pequeno e está isolado abaixo: o nome
 * do dinheiro que a empresa recebe (spread do assessor, RN-170; receita do
 * fornecedor, RN-173) e a coluna de fornecedor, que no painel do próprio
 * fornecedor seria a mesma empresa em toda linha.
 */

type Participante = Exclude<DashboardIcms, 'CLIENTE'>;

/**
 * RN-170 vs. RN-173 — não é o mesmo dinheiro.
 *
 * O assessor ganha o spread entre o que o fornecedor cobra e o que o cliente
 * paga; o fornecedor recebe pelo crédito que transferiu. Chamar os dois de
 * "comissão" faria a tela do fornecedor descrever a receita dele como comissão
 * de terceiro.
 */
const ROTULO_DO_GANHO: Readonly<Record<Participante, string>> = {
  ASSESSOR: 'Comissão',
  FORNECEDOR: 'Receita',
};

const DETALHE_DO_GANHO: Readonly<Record<Participante, string>> = {
  ASSESSOR: 'Spread entre o custo do fornecedor e o valor pago pelo cliente (RN-170).',
  FORNECEDOR: 'Receita pelos créditos transferidos aos clientes (RN-173).',
};

/**
 * O cliente abre a tela dele: o assessor entra no cálculo e no cadastro do
 * cliente que acompanha; o fornecedor, no cálculo de quem compra o seu crédito
 * — só leitura. Quem decide o que cada um pode fazer lá é a tela do cliente.
 */
function celulaDeCliente(linha: LinhaDoParticipante): JSX.Element {
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Link
        to={`/clientes/${linha.clienteId}`}
        className="underline-offset-4 hover:text-acento-ciano hover:underline"
      >
        {linha.clienteNome ?? (
          // Cliente sem nome no cadastro aparece pelo id, não por um rótulo
          // inventado: duas linhas com "Cliente sem nome" seriam indistinguíveis.
          <span className="valor-fiscal text-xs text-conteudo-secundario">{linha.clienteId}</span>
        )}
      </Link>
      {linha.simulado && (
        <Selo tom="informativo" tamanho="pequeno" contexto="Origem da linha">
          projeção
        </Selo>
      )}
    </span>
  );
}

function colunasDoParticipante(
  dashboard: Participante,
): readonly ColunaTabela<LinhaDoParticipante>[] {
  const colunaFornecedor: ColunaTabela<LinhaDoParticipante> = {
    chave: 'fornecedor',
    titulo: 'Fornecedor',
    larguraMinima: '12rem',
    celula: (linha) =>
      linha.fornecedorNome ?? <span className="text-conteudo-secundario">sem nome cadastrado</span>,
  };

  return [
    {
      chave: 'cliente',
      titulo: 'Cliente',
      larguraMinima: '14rem',
      celula: celulaDeCliente,
    },
    // No painel do fornecedor a coluna seria a própria empresa em toda linha.
    ...(dashboard === 'ASSESSOR' ? [colunaFornecedor] : []),
    {
      chave: 'status',
      titulo: 'Contrato',
      larguraMinima: '11rem',
      celula: (linha) => <SeloDeContrato status={linha.status} />,
    },
    {
      chave: 'valorUtilizacao',
      titulo: 'Valor de utilização',
      numerica: true,
      celula: (linha) => <ValorFiscal valor={linha.valorUtilizacao} />,
    },
    {
      chave: 'beneficioEconomico',
      titulo: 'Economia do cliente',
      numerica: true,
      celula: (linha) => <ValorFiscal valor={linha.beneficioEconomico} variante="economia" />,
    },
    {
      chave: 'comissao',
      titulo: ROTULO_DO_GANHO[dashboard],
      numerica: true,
      celula: (linha) => <ValorFiscal valor={linha.comissao} />,
    },
  ];
}

export function PainelParticipante(props: {
  readonly dashboard: Participante;
  readonly ano: number;
  /** O filtro do ano, na linha do "Resumo de …". */
  readonly seletorDeAno?: ReactNode;
}): JSX.Element {
  const painel = usePainelDoParticipante(props.dashboard, props.ano);

  // O cabeçalho do Resumo sai antes dos dados: trocar o ano não faz o filtro
  // sumir enquanto o ano novo carrega. RN-180 — o SPED é o do ano anterior.
  const cabecalho = (
    <TituloDeSecao
      id="titulo-resumos"
      titulo={`Resumo de ${props.ano}`}
      descricao={`Apurado sobre o SPED de ${props.ano - 1} — o ano fiscal é sempre o anterior ao da operação (RN-180).`}
      {...(props.seletorDeAno === undefined ? {} : { acao: props.seletorDeAno })}
    />
  );

  if (painel.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {cabecalho}
        <EstadoCarregando variante="painel" contexto={`o painel de ${props.ano}`} />
      </div>
    );
  }

  if (painel.isError) {
    return (
      <div className="flex flex-col gap-4">
        {cabecalho}
        <EstadoErro
          titulo="Não foi possível carregar o painel"
          // Mensagem do servidor, nunca texto fixo (dívida #50, RN-627/RN-628).
          mensagem={mensagemDoErro(painel.error)}
          aoTentarNovamente={() => void painel.refetch()}
        />
      </div>
    );
  }

  return (
    <ParticipanteCarregado dashboard={props.dashboard} dados={painel.data} cabecalho={cabecalho} />
  );
}

function ParticipanteCarregado(props: {
  readonly dashboard: Participante;
  readonly dados: PainelDoParticipante;
  readonly cabecalho: ReactNode;
}): JSX.Element {
  const { dashboard, dados } = props;
  const colunas = useMemo(() => colunasDoParticipante(dashboard), [dashboard]);
  // Colunas descritivas entre o cabeçalho de linha e os três totais.
  const descritivas = dashboard === 'ASSESSOR' ? 2 : 1;

  return (
    <div className="flex flex-col gap-8">
      <section aria-labelledby="titulo-resumos" className="flex flex-col gap-4">
        {props.cabecalho}

        {/* RN-169 — o ano-alvo ainda quase não tem contrato; as linhas vêm dos
            vínculos de cadastro. Dizer isso antes dos números é o que separa
            projeção de apuração. */}
        {dados.projetado && (
          <Aviso tom="informacao" titulo={`${dados.ano} ainda é projeção`}>
            O ano que vem ainda não tem contratos fechados. As linhas marcadas como{' '}
            <strong>projeção</strong> vêm dos vínculos de cadastro e do SPED de {dados.anoSped}, e
            mudam conforme os contratos forem fechados (RN-169).
          </Aviso>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <CartaoIndicador
            rotulo="Valor de utilização"
            valor={<ValorFiscal valor={dados.geral.valorUtilizacaoTotal} tamanho="grande" />}
            detalhe="Crédito transferido no ano, somando todos os clientes."
          />
          <CartaoIndicador
            rotulo="Economia dos clientes"
            valor={
              <ValorFiscal
                valor={dados.geral.beneficioEconomicoTotal}
                variante="economia"
                tamanho="grande"
              />
            }
            detalhe="Quanto os clientes deixaram de pagar com o deságio."
          />
          {/*
            Comissão negativa não é resultado: é entrada faltando.
            
            O ganho é o spread entre o que o fornecedor cobra e o que o cliente
            paga (RN-170). Quando o deságio do fornecedor não está preenchido no
            contrato, o spread vira o valor do cliente com o sinal trocado — um
            número grande, negativo e convincente, calculado sobre um zero que
            ninguém informou.
            
            Na base migrada do legado isso vale para os 895 contratos:
            `desagio_venda_fornecedor` é 0 em todos. Mostrar o número seria
            repetir o erro que este projeto passou inteiro evitando — número com
            cara de apurado, apoiado em dado que não existe.
          */}
          <CartaoIndicador
            rotulo={ROTULO_DO_GANHO[dashboard]}
            valor={
              dados.geral.comissaoTotal < 0 ? (
                <span className="text-2xl text-conteudo-sutil">Sem dado</span>
              ) : (
                <ValorFiscal valor={dados.geral.comissaoTotal} tamanho="grande" />
              )
            }
            detalhe={
              dados.geral.comissaoTotal < 0
                ? 'O deságio do fornecedor não está preenchido nos contratos deste ano, então o spread não pode ser apurado.'
                : DETALHE_DO_GANHO[dashboard]
            }
          />
          <CartaoIndicador
            rotulo="Conversão"
            // RN-171 — este **é** percentual (escala 0–100), e por isso leva `%`.
            valor={
              <ValorPercentual
                percentual={dados.geral.percentualConversaoPercentual}
                tamanho="grande"
              />
            }
            detalhe={`${dados.geral.totalContratosFechados} contrato(s) fechado(s) em ${dados.geral.totalCalculos} cálculo(s).`}
          />
          {/*
            RN-172 / RN-174 — dinheiro, não percentual. O campo se chama
            `mediaPorContratoFechadoReais` justamente porque o nome legado
            (`mediaDesagioVendido`) fazia a tela anexar um `%` a um valor em
            reais — a forma exata do RN-078 reaparecendo.
          */}
          {/* Deriva da comissão: sem o deságio do fornecedor, herda o mesmo vazio. */}
          <CartaoIndicador
            rotulo="Média por contrato fechado"
            valor={
              dados.geral.mediaPorContratoFechadoReais < 0 ? (
                <span className="text-2xl text-conteudo-sutil">Sem dado</span>
              ) : (
                <ValorFiscal valor={dados.geral.mediaPorContratoFechadoReais} tamanho="grande" />
              )
            }
            detalhe={
              dados.geral.mediaPorContratoFechadoReais < 0
                ? 'Depende do spread, que não pode ser apurado sem o deságio do fornecedor.'
                : `Em reais: ${ROTULO_DO_GANHO[dashboard].toLowerCase()} dos meses fechados dividida pelos contratos fechados. Não é percentual de deságio.`
            }
          />
          <CartaoMesesAproveitados
            meses={dados.geral.mesesAproveitamento}
            metricasIndisponiveis={dados.metricasIndisponiveis}
          />
        </div>
      </section>

      <section aria-labelledby="titulo-linhas" className="flex min-w-0 flex-col gap-4">
        <TituloDeSecao
          id="titulo-linhas"
          titulo="Clientes"
          descricao={
            dashboard === 'ASSESSOR'
              ? 'Uma linha por cliente e fornecedor: o mesmo cliente aparece uma vez para cada fornecedor que atendeu o ano dele.'
              : 'Uma linha por cliente atendido no ano.'
          }
        />

        {dados.linhas.length === 0 ? (
          <EstadoVazio
            titulo={`Nenhum cliente apurado em ${dados.ano}`}
            descricao={`Não há contrato ativo nem SPED de ${dados.anoSped} vinculado a esta empresa neste ano. Assim que houver, as linhas aparecem aqui.`}
          />
        ) : (
          <Tabela
            legenda={`Clientes atendidos em ${dados.ano}, sobre o SPED de ${dados.anoSped}`}
            colunas={colunas}
            dados={dados.linhas}
            // Cliente e fornecedor juntos: o par é a granularidade da linha, e o
            // mesmo cliente pode aparecer com dois fornecedores (RN-020).
            chaveDaLinha={(linha) => `${linha.clienteId}·${linha.fornecedorId}`}
            chaveDeCabecalhoDaLinha="cliente"
            rodape={
              <tr>
                <CelulaDeTotal cabecalhoDaLinha>Total geral</CelulaDeTotal>
                <CelulaDeTotal colSpan={descritivas}>
                  <span className="sr-only">Sem total para as colunas descritivas</span>
                </CelulaDeTotal>
                <CelulaDeTotal numerica>
                  <ValorFiscal valor={dados.geral.valorUtilizacaoTotal} />
                </CelulaDeTotal>
                <CelulaDeTotal numerica>
                  <ValorFiscal valor={dados.geral.beneficioEconomicoTotal} variante="economia" />
                </CelulaDeTotal>
                <CelulaDeTotal numerica>
                  {dados.geral.comissaoTotal < 0 ? (
                    <span className="text-conteudo-sutil">—</span>
                  ) : (
                    <ValorFiscal valor={dados.geral.comissaoTotal} />
                  )}
                </CelulaDeTotal>
              </tr>
            }
          />
        )}

        <NotaDeMetricasIndisponiveis metricasIndisponiveis={dados.metricasIndisponiveis} />
      </section>
    </div>
  );
}
